import { motion } from 'framer-motion';
import { ArrowRight, CalendarClock, ClipboardList, FileText } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { ActivityTimeline } from '../components/dashboard/ActivityTimeline';
import { DashboardHero } from '../components/dashboard/DashboardHero';
import { QuickActions } from '../components/dashboard/QuickActions';
import { Alert } from '../components/ui/Alert';
import { staggerContainer, staggerItem } from '../components/ui/motionVariants';
import { useMyBookings } from '../lib/hooks/useBookings';
import { useClientQuestionnaires } from '../lib/hooks/useClientQuestionnaires';
import { selectCurrentReport, useClientReports } from '../lib/hooks/useClientReports';

function formatSessionDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

type StatTileTone = 'brand' | 'gold' | 'success';

const STAT_TILE_TONE_CLASSES: Record<StatTileTone, string> = {
  brand: 'bg-primary/10 text-primary ring-primary/20',
  gold: 'bg-accent/15 text-accent ring-accent/20',
  success: 'bg-success/10 text-success ring-success/20',
};

interface StatTileProps {
  icon: LucideIcon;
  label: string;
  value: string;
  to: string;
  tone?: StatTileTone;
}

function StatTile({ icon: Icon, label, value, to, tone = 'brand' }: StatTileProps): JSX.Element {
  return (
    <Link to={to} className="block h-full">
      <motion.div
        whileHover={{ y: -2 }}
        transition={{ type: 'spring', stiffness: 400, damping: 25 }}
        className="group flex flex-col gap-3 p-5 rounded-2xl border border-border bg-card hover:border-primary/30 transition-all h-full ring-1 ring-foreground/5 shadow-soft hover:shadow-glow-brand"
      >
        <div className="flex items-center justify-between">
          <div
            className={`flex items-center justify-center w-10 h-10 rounded-2xl ring-1 ${STAT_TILE_TONE_CLASSES[tone]}`}
          >
            <Icon className="w-5 h-5" />
          </div>
          <ArrowRight className="w-4 h-4 text-muted-foreground/40 group-hover:text-primary group-hover:translate-x-0.5 transition-all" />
        </div>
        <div className="mt-2">
          <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
            {label}
          </p>
          <p className="text-sm font-medium text-foreground mt-1">{value}</p>
        </div>
      </motion.div>
    </Link>
  );
}

export function DashboardPage(): JSX.Element {
  const { client, activeMember } = useAuth();
  const { data: questionnaires } = useClientQuestionnaires();
  const { data: reports } = useClientReports();
  const { data: bookings } = useMyBookings();
  const pendingCount = questionnaires?.filter((q) => q.status !== 'completed').length ?? 0;

  const latestReport = selectCurrentReport(reports, client?.languagePref);
  const reportReady = latestReport?.status === 'GENERATED' || latestReport?.status === 'DELIVERED';
  const reportValue = !latestReport
    ? 'Not started yet'
    : reportReady
      ? 'Ready to view'
      : latestReport.status === 'FAILED'
        ? 'Generation failed'
        : 'Preparing…';

  const nextBooking = bookings
    ?.filter((b) => b.status === 'CONFIRMED' && new Date(b.startsAt).getTime() >= Date.now())
    .sort((a, b) => new Date(a.startsAt).getTime() - new Date(b.startsAt).getTime())[0];
  const bookingValue = nextBooking
    ? formatSessionDateTime(nextBooking.startsAt)
    : 'No sessions booked';

  return (
    <motion.div
      className="flex flex-col gap-8"
      variants={staggerContainer()}
      initial="hidden"
      animate="show"
    >
      <DashboardHero firstName={activeMember?.firstName ?? client?.firstName} />

      {pendingCount > 0 && (
        <motion.div variants={staggerItem}>
          <Link to="/app/questionnaires" className="block">
            <Alert tone="info">
              <div className="flex items-center justify-between gap-3 w-full">
                <span className="font-medium">
                  {pendingCount === 1
                    ? 'Action required: You have a questionnaire to complete before your next session.'
                    : `Action required: You have ${pendingCount} questionnaires to complete before your next session.`}
                </span>
                <ArrowRight className="w-4 h-4 shrink-0" />
              </div>
            </Alert>
          </Link>
        </motion.div>
      )}

      <div className="flex flex-col gap-3">
        <motion.h2
          variants={staggerItem}
          className="text-xl font-display font-semibold tracking-tight mt-2"
        >
          Quick Actions
        </motion.h2>
        <QuickActions />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 flex flex-col gap-6">
          <motion.h2
            variants={staggerItem}
            className="text-xl font-display font-semibold tracking-tight"
          >
            Your Overview
          </motion.h2>
          <motion.div
            variants={staggerItem}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
          >
            <StatTile
              icon={FileText}
              label="Report Status"
              value={reportValue}
              to="/app/report"
              tone="brand"
            />
            <StatTile
              icon={ClipboardList}
              label="Questionnaires"
              value={pendingCount > 0 ? `${pendingCount} pending` : 'All caught up'}
              to="/app/questionnaires"
              tone="gold"
            />
            <StatTile
              icon={CalendarClock}
              label="Next session"
              value={bookingValue}
              to="/app/bookings"
              tone="success"
            />
          </motion.div>
          <motion.div
            variants={staggerItem}
            className="mt-4 p-6 rounded-2xl border border-border bg-card ring-1 ring-foreground/5 shadow-soft flex flex-col gap-4"
          >
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-lg font-display font-semibold">Upcoming Milestone</h3>
                <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                  {nextBooking
                    ? 'You have a scheduled coaching session coming up. Review your report to prepare.'
                    : 'No upcoming sessions. Book a session to continue your progress.'}
                </p>
              </div>
              {nextBooking && (
                <div className="bg-success/10 text-success px-3 py-1 rounded-full text-xs font-semibold">
                  Confirmed
                </div>
              )}
            </div>
            {nextBooking && (
              <div className="flex flex-col sm:flex-row items-center gap-4 pt-4 border-t border-border mt-2">
                <div className="flex-1 flex items-center gap-3 w-full">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold">
                    CG
                  </div>
                  <div>
                    <p className="text-sm font-medium">Coach Gamma</p>
                    <p className="text-xs text-muted-foreground">{bookingValue}</p>
                  </div>
                </div>
                <Link
                  to="/app/bookings"
                  className="w-full sm:w-auto text-center px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-full hover:bg-primary/90 transition-colors"
                >
                  Manage Booking
                </Link>
              </div>
            )}
          </motion.div>
        </div>
        <div className="lg:col-span-1">
          <ActivityTimeline />
        </div>
      </div>
    </motion.div>
  );
}
